import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Paper,
  Typography,
} from "@mui/material";
import { ArrowRight } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Container, Loading, PaymentMethods } from "..";
import StepContainer from "../StepContainer";
import MerchantInfo from "../merchantInfo";
import ServiceChargeSummary from "../ServiceChargeSummary";
import ConfiguredFields from "../fields/ConfiguredFields";
import StackList from "../stackList";
import CatalogueBrowser from "../SchoolFee/CatalogueBrowser";
import CatalogueFeeSelect from "../SchoolFee/CatalogueFeeSelect";
import FeeSelect from "../SchoolFee/FeeSelect";
import ManualIdentityForm from "../SchoolFee/ManualIdentityForm";
import RegistrationIdentityForm from "../SchoolFee/RegistrationIdentityForm";
import SchoolFeeAmountForm from "../SchoolFee/SchoolFeeAmountForm";
import SchoolFeeContactStep from "../SchoolFee/SchoolFeeContactStep";
import StudentSearchForm from "../SchoolFee/StudentSearchForm";
import {
  priceSearch,
  useQueryPaymentMethods,
  usePriceMutation,
} from "../../api/queries";
import { usePayMutation } from "../../api/queries/usePayMutation";
import { usePayment } from "../../hooks";
import { useGlobalContext } from "../../context/globalContext";
import {
  SCHOOL_FEE_STEPS,
  SchoolFeeProvider,
  SchoolFeeStep,
  useSchoolFee,
} from "../../context/schoolFeeContext";
import { DEFAULT_SERVICE_LOGO, LAPSED_SESSION_CODE } from "../../constants";
import { isCompleteNationalNumber } from "../../constants/phoneCountries";
import { PaymentMethod } from "../../models/payment";
import { SchoolFeeScenario } from "../../models/schoolFee";
import {
  formatCurrency,
  getFailureDisplay,
  getStorageToken,
  isErrorResponse,
  unclaimedFields,
} from "../../utils";
import type { FieldClaims } from "../../utils";
import { isWithinFee, wireAmount } from "../../utils/schoolFeeAmount";
import { isCompleteIdentity } from "../../utils/schoolFeeIdentity";
import {
  isWithinPropertyBound,
  oversizedCdataValues,
} from "../../utils/cdataBounds";
import { payerPhoneNumber } from "../../constants/phoneCountries";
import type { FlowTemplateProps } from "./DepositFundTemplate";

// The searched journey uses the whole named-step sequence the 12-07 context
// declares. It is re-exported rather than redeclared so a step added there
// cannot be missed here.
export const SCHOOL_FEE_SEARCH_STEPS = SCHOOL_FEE_STEPS;

/**
 * The field codes this template draws with its own inputs, per
 * docs/guidelines/configured-fields-ownership.md.
 *
 * ⛔ RULED 2026-09-09 by the maintainer: the searched journey CLAIMS the
 * identity codes it draws, so a service configuring them does not ask the
 * payer a second time. This applies the guideline's school-fee table as
 * written. An earlier revision of this file claimed NOTHING and argued from
 * rule 2b's destination test -- that reading is superseded, and the ruling is
 * recorded at D-12-08-A in the 12-08 summary.
 *
 * ⛔ TWO of the table's five codes, not five, and the reason is the guideline's
 * OWN rule 3: "a claimed code that is not actually drawn is a test failure".
 * This journey draws a matricule box (SVCNMB) and a single name box (CUSNAME)
 * to search with. It draws NO date-of-birth input and NO separate given/family
 * name inputs, so CUSDOB, CUSFIRSTNAME and CUSLASTNAME cannot be claimed here
 * -- claiming them would be a promise to render that is never kept, and the
 * rule-3 test below proves it. Those three belong to the MANUAL and
 * REGISTRATION journeys (12-09), which really do draw them.
 *
 * ⚠ What this costs, recorded because it is silent: a claimed code's configured
 * field is removed from the slot, so its answer never reaches `cdata` and never
 * reaches the school. Claims are CONSUMING and one-per-occurrence, so a service
 * declaring SVCNMB twice still shows the second with its own label -- but the
 * first is gone. If a school marks such a field mandatory, it is the template's
 * own input that must satisfy it (guideline rule 5: a claimed field's
 * mandatoriness becomes the template's).
 */
export const SCHOOL_FEE_SEARCH_CLAIMS: FieldClaims = ["SVCNMB", "CUSNAME"];

// Journey B. Same five steps as the searched journey -- what changes is how
// the identity step is answered, not how many there are.
export const SCHOOL_FEE_MANUAL_STEPS = SCHOOL_FEE_STEPS;

/**
 * Journey B draws the whole student identity itself: a matricule, a given
 * name, a family name and a date of birth, each feeding the pay request
 * directly. So it claims all five codes the guideline's table B lists.
 *
 * `CUSNAME` sits beside the split pair because the widget registry draws it as
 * the SAME two inputs -- a service declaring either spelling is answered by
 * what is already on screen.
 *
 * ⛔ Phone and email are NOT claimed, on this journey as on every other: ours
 * is the instrument account debited and the receipt address, a configured one
 * is the parent's contact. Same code, different subject and destination, so
 * rule 2b says it is a different field and two boxes is correct.
 */
export const SCHOOL_FEE_MANUAL_CLAIMS: FieldClaims = [
  "SVCNMB",
  "CUSNAME",
  "CUSFIRSTNAME",
  "CUSLASTNAME",
  "CUSDOB",
];

// Journey C. NO amount step: the registration fee is paid in full, so there is
// no number for the payer to enter and no screen on which to enter it (D-05).
export const SCHOOL_FEE_REGISTRATION_STEPS: readonly SchoolFeeStep[] = [
  "identity",
  "fee",
  "method",
  "confirm",
];

/**
 * Journey C claims table B's codes MINUS `SVCNMB`.
 *
 * ⛔ It cannot claim the matricule, because it does not draw one: a student
 * being registered has no student number yet and the server refuses a
 * registration payment that carries one. Claiming a code this template never
 * renders is precisely the rule-3 failure the guard exists to catch.
 *
 * ⚠ The consequence, recorded because it looks like a defect: unclaimed means
 * rule 4 sends a configured `SVCNMB` to the slot, so a registration service
 * configured with one shows the payer a student-number box. That is a
 * CONFIGURATION defect on the school's side and is raised as one -- claiming
 * the code to make the box disappear would both break rule 3 and silently
 * delete a question the school asked.
 */
export const SCHOOL_FEE_REGISTRATION_CLAIMS: FieldClaims = [
  "CUSNAME",
  "CUSFIRSTNAME",
  "CUSLASTNAME",
  "CUSDOB",
];

type SchoolFeeJourneyState = ReturnType<typeof useSchoolFee>;

type IdentityPayload = (
  journey: SchoolFeeJourneyState
) => Record<string, unknown>;

type PanelRenderer = (
  step: SchoolFeeStep,
  methods: PaymentMethod[],
  configuredFields: React.ReactNode
) => React.ReactNode;

type JourneyProps = FlowTemplateProps & {
  steps: readonly SchoolFeeStep[];
  claims: FieldClaims;
  // Held as CLIENT-SIDE state and never put on the wire (D-HARM-7): the server
  // derives the journey from the service's own type/variant/searchability --
  // the same triple the registry already used to choose this template. A
  // request that also declared it would be asserting the decision back.
  scenario: SchoolFeeScenario;
  // The half of the pay request that differs between the three school-fee
  // journeys: searched pays for the pupil a session resolved, the other two
  // for an identity the payer declared.
  identityPayload: IdentityPayload;
  renderPanel: PanelRenderer;
  // ⛔ Journey-specific knowledge lives HERE, in the template that owns it, and
  // not in schoolFeeContext. D-12-08-D's open question was whether a second and
  // third journey would each need their own transition rule in the context and
  // force the move to a reducer; they do not, because the differences are
  // expressible as data the shared machine is given.
  //
  // True where the step's own result ends it -- journey A's search hit. False
  // where the payer fills a form and presses Continue.
  identitySelfAdvances?: boolean;
  // What a completed identity step means on this journey. The context cannot
  // answer it: `canEnter` treats a placement or a matricule as identified,
  // which is right for navigation and far too loose for a payment.
  identitySatisfied?: (journey: SchoolFeeJourneyState) => boolean;
  // Reasons this journey cannot pay that no screen can fix, so they stop the
  // payment rather than the navigation.
  payBlockers?: (journey: SchoolFeeJourneyState) => string[];
  // Per-journey wording for a shared step. "Find the student" is right for a
  // roster search and wrong for a catalogue browse.
  stepTitles?: Partial<Record<SchoolFeeStep, string>>;
};

// The step after this one, or null at the end. The sequence is data, so the
// journey never adds a number to a step.
const nextStep = (
  steps: readonly SchoolFeeStep[],
  step: SchoolFeeStep
): SchoolFeeStep | null => steps[steps.indexOf(step) + 1] ?? null;

// A field the payer typed into and then cleared is not an answer, so it is
// dropped rather than sent empty. Returns nothing at all when there is nothing
// to say, so the pay request omits the key entirely. Mirrors the deposit
// template's helper rather than inventing a second shape for one mechanism.
const answeredCdata = (values: Record<string, string>) => {
  const answered = Object.entries(values).filter(
    ([, value]) => value.trim() !== ""
  );

  return answered.length > 0 ? { cdata: Object.fromEntries(answered) } : {};
};

// D-04's designated slot, filtered through the template's claims exactly as
// the deposit journey does.
const ConfiguredFieldsSlot = ({
  service,
  claims,
  onBlocked,
  onIncomplete,
  values,
  onChange,
}: {
  service: FlowTemplateProps["service"];
  claims: FieldClaims;
  onBlocked: (blocking: string[]) => void;
  // Mandatory, drawable fields not yet correctly answered -- distinct from
  // onBlocked, whose "cannot be paid here" wording is only true for a field
  // with nowhere to answer it at all.
  onIncomplete: (incomplete: string[]) => void;
  values: Record<string, string>;
  onChange: (fieldId: string, value: string) => void;
}) => {
  const fields = unclaimedFields(service?.submitFields ?? [], claims);

  if (fields.length === 0) return null;

  return (
    <Box mb={4}>
      <ConfiguredFields
        fields={fields}
        values={values}
        onChange={onChange}
        onUnsupported={onBlocked}
        onIncomplete={onIncomplete}
      />
    </Box>
  );
};

/**
 * The step machine the three school-fee journeys share, and the shared spine
 * 12-09's two templates reuse without restructuring.
 *
 * ⛔ It drives `schoolFeeContext`'s NAMED steps through `goTo`/`canEnter`/
 * `back`. It does NOT index a step array with a bare integer from
 * paymentContext, and it never calls a raw step setter -- copying the deposit
 * template faithfully on that point would import a payment-journey-step-
 * integrity violation into new code, where the baseline exemption does not
 * reach.
 */
const SchoolFeeJourney = ({
  service,
  theme,
  corporateId,
  steps,
  claims,
  scenario,
  identityPayload,
  renderPanel,
  identitySelfAdvances = false,
  identitySatisfied,
  payBlockers,
  stepTitles,
}: JourneyProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const token = getStorageToken();
  const journey = useSchoolFee();
  const {
    currentStep,
    goTo,
    back,
    selectedFee,
    amount,
    paymentMethodId,
    setPaymentMethodId,
    setScenario,
    instrumentAccountNumber,
    instrumentCountry,
    email,
    expireSession,
  } = journey;

  const [error, setError] = useState("");
  const [lapsed, setLapsed] = useState(false);
  const [blockedFieldIds, setBlockedFieldIds] = useState<string[]>([]);
  // A mandatory configured field the payer has typed something into, but
  // that fails its own regex. Kept apart from the `unanswered` check just
  // below, which only catches a field left blank.
  const [incompleteFieldIds, setIncompleteFieldIds] = useState<string[]>([]);
  // Owned by the journey, not by the slot: the answers have to reach the pay
  // call, and state held inside the slot cannot.
  const [configuredValues, setConfiguredValues] = useState<
    Record<string, string>
  >({});

  const {
    setTotalAmount,
    setTotalFees,
    setInstrumentFee,
    setRegistrationFee,
    setCurrency,
  } = useGlobalContext();

  // PaymentMethods reads and writes paymentContext, which service.tsx mounts
  // around every template. The bridge to the school-fee journey is explicit
  // and one-way, because `canEnter("confirm")` gates on the id being set.
  const { selectedMethod } = usePayment();

  // Once, at mount. It selects this journey's step sequence and nothing else --
  // ⛔ it is never read into a request body.
  useEffect(() => {
    setScenario(scenario);
  }, [scenario, setScenario]);

  useEffect(() => {
    setPaymentMethodId(selectedMethod?.id ? String(selectedMethod.id) : "");
  }, [selectedMethod, setPaymentMethodId]);

  // Going back to the search step and choosing a different pupil must not
  // carry the previous one's answers onto this payment.
  useEffect(() => {
    if (currentStep === "identity") setConfiguredValues({});
  }, [currentStep]);

  const payMutation = usePayMutation({ authorisation: token! });
  const priceMutation = usePriceMutation({ authorisation: token! });

  const paymentMethods = useQueryPaymentMethods({
    corporateId,
    enabled: !!token,
    authorisation: token || "",
  });

  const isLoading = payMutation.isLoading || priceMutation.isLoading;

  const showFailure = (failure: unknown) =>
    setError(getFailureDisplay(failure, t, i18n.language).message);

  // Everything wrong with the configured-fields slot, which lives on the
  // METHOD step. These are reported and enforced THERE, beside the inputs that
  // fix them: a payer told to "answer every required question above" on the
  // confirmation screen is being pointed at a screen they have already left.
  const methodBlockers = (): string[] => {
    const blockers: string[] = [];

    if (blockedFieldIds.length > 0) {
      blockers.push("configuredFields.unsupported.blocking");
    }

    const unanswered = unclaimedFields(service?.submitFields ?? [], claims)
      .filter(field => field.mandatory)
      .filter(field => (configuredValues[field.fieldId] ?? "").trim() === "");

    // unanswered catches blank; incompleteFieldIds catches typed-but-invalid
    // against the field's own regex. Same payer-facing copy either way: both
    // are "answer the question above correctly", not "contact the provider".
    if (unanswered.length > 0 || incompleteFieldIds.length > 0) {
      blockers.push("schoolfee.errors.configured_field_required");
    }

    // Bounded on the client as well as the server so the payer is told which
    // answer is too long. ⛔ Refused, never truncated: shortening a payer's
    // answer to make it fit changes what they said.
    if (!isWithinPropertyBound(configuredValues)) {
      blockers.push("schoolfee.errors.cdata_too_many");
    }

    if (oversizedCdataValues(configuredValues).length > 0) {
      blockers.push("schoolfee.errors.cdata_too_long");
    }

    return blockers;
  };

  // What this JOURNEY cannot pay without, as against what this SCREEN cannot
  // proceed without. Nothing on the method step fixes either, so they stop the
  // payment rather than the navigation and are said on the screen where the
  // payment would have been made.
  const stepBlockers = methodBlockers();
  const blockers = [...stepBlockers, ...(payBlockers?.(journey) ?? [])];

  // Each step's own output, so no step is entered without its predecessor's.
  const stepSatisfied = (step: SchoolFeeStep): boolean => {
    switch (step) {
      case "identity":
        // Absent on a journey whose identity step ends itself: journey A has
        // no Continue button to guard.
        return identitySatisfied ? identitySatisfied(journey) : false;
      case "fee":
        return !!selectedFee;
      case "amount":
        // Only an explicit false asks for the full amount, as on the amount form.
        return isWithinFee(amount, selectedFee?.amount ?? "", service?.amount, {
          partialAllowed: selectedFee?.supports_partial_payments !== false,
        });
      case "method":
        return (
          !!paymentMethodId &&
          isCompleteNationalNumber(instrumentAccountNumber, instrumentCountry) &&
          stepBlockers.length === 0
        );
      case "confirm":
        // Re-checked rather than inherited from `canEnter`. Reaching confirm
        // proves the method step was satisfied once; it is cheap to say so
        // here too, and it is the step-integrity rule written as a condition.
        return (
          !!paymentMethodId &&
          isCompleteNationalNumber(instrumentAccountNumber, instrumentCountry) &&
          blockers.length === 0
        );
      default:
        return false;
    }
  };

  const canSubmit = stepSatisfied(currentStep) && !isLoading;

  const storePrice = (data: {
    total_fees: string;
    total_amount: string;
    instrument_fee?: string;
    registration_fee?: string;
  }) => {
    setTotalFees(String(data.total_fees));
    setTotalAmount(String(data.total_amount));
    setInstrumentFee(String(data.instrument_fee ?? ""));
    setRegistrationFee(String(data.registration_fee ?? ""));
  };

  // ⚠ The price call is NOT harmonized: D-HARM-3 and D-HARM-4 scope to the pay
  // request, so it keeps naming the service the way it always has. After this
  // plan the portal names the service one way to price and another to pay
  // (TODO-266), which is why these are two payloads and not one shared literal.
  const pricePayload = () => ({
    amount: wireAmount(amount),
    corporate_subsidiary_id: service?.corporate_subsidiary_id,
    payment_method_id: Number(paymentMethodId),
    corporate_service_id: service?.id,
  });

  const submit = () => {
    if (!canSubmit) return;
    setError("");

    if (currentStep === "confirm") {
      payMutation.mutate(
        {
          amount: wireAmount(amount),
          // D-HARM-13: the ONE service identifier, a string, in the body.
          // ⛔ corporate_subsidiary_id, corporate_service_id and channel are
          // deliberately absent (D-HARM-14 / D-HARM-15). The deposit rail still
          // carries them for compatibility with the old server; the school-fee
          // route it is replacing has been deleted, so there is no older
          // contract here to stay compatible with.
          core_service_id: service?.core_service_id,
          payment_method_id: Number(paymentMethodId),
          instrument_account_number: payerPhoneNumber(instrumentAccountNumber),
          // Omitted entirely when blank -- "" is not an address, and the
          // contract's `format: email` would refuse it.
          ...(email.trim() !== ""
            ? { notification_email: email.trim() }
            : {}),
          ...identityPayload(journey),
          ...answeredCdata(configuredValues),
        },
        {
          onSuccess: data => {
            setCurrency(data.currency);
            navigate(
              `transaction/${data.payment_item_id}?${priceSearch(pricePayload())}`,
              { state: { phoneNumber: payerPhoneNumber(instrumentAccountNumber) } }
            );
          },
          onError: failure => {
            // The one recoverable failure: the search session lapsed. The
            // pupil and the fee stand; only the server-side session is gone.
            if (failure?.code === LAPSED_SESSION_CODE) {
              setLapsed(true);
              expireSession();
              return;
            }

            showFailure(failure);
          },
        }
      );
      return;
    }

    if (currentStep === "method") {
      priceMutation.mutate(pricePayload(), {
        onSuccess: data => {
          // The price call resolves WITH an error envelope rather than
          // throwing, so this guard is live.
          if (isErrorResponse(data)) {
            showFailure(data);
            return;
          }
          storePrice(data);
          goTo("confirm");
        },
        onError: showFailure,
      });
      return;
    }

    const next = nextStep(steps, currentStep);
    if (next) goTo(next);
  };

  if (paymentMethods?.isLoading) return <Loading />;

  const methods = (paymentMethods?.data as PaymentMethod[]) || [];

  // On journey A the search result is what advances the identity step, so a
  // Continue button there would offer a way past a step whose output nothing
  // has produced. The two declared-identity journeys have no such moment: the
  // payer fills a form, and something has to accept it.
  const showsActions = !(currentStep === "identity" && identitySelfAdvances);

  // ⚠ The Back button was shown whenever `canEnter("fee")` held, which on the
  // first step of a journey is a button that does nothing. It is now the
  // honest question -- is there a step behind this one, IN THIS JOURNEY'S
  // sequence -- which is also the only form of it that survives registration
  // having four steps where the others have five.
  const hasPreviousStep = steps.indexOf(currentStep) > 0;

  const configuredFields =
    currentStep === "method" ? (
      <ConfiguredFieldsSlot
        service={service}
        claims={claims}
        onBlocked={setBlockedFieldIds}
        onIncomplete={setIncompleteFieldIds}
        values={configuredValues}
        onChange={(fieldId, value) => {
          setLapsed(false);
          setConfiguredValues(previous => ({ ...previous, [fieldId]: value }));
        }}
      />
    ) : null;

  return (
    <Container
      justifyContent="center"
      alignItems="center"
      height="70vh"
      sx={{ borderColor: theme?.color_primary }}
    >
      <Box>
        <StepContainer
          title={t("schoolfee.title")}
          description={t(
            stepTitles?.[currentStep] ?? `schoolfee.steps.${currentStep}`
          )}
        />

        {lapsed && (
          <Alert severity="warning" sx={{ mx: 5, mb: 2 }} role="alert">
            <AlertTitle>{t("schoolfee.errors.session_expired_title")}</AlertTitle>
            {t("schoolfee.errors.CPS-408-101")}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ margin: 5 }}>
            <AlertTitle>{t("errors.title")}</AlertTitle>
            {error}
          </Alert>
        )}

        {/* The slot's own problems are shown on the step that draws it; the
            lapsed-session one only where the payment would have been made. */}
        {(currentStep === "method" || currentStep === "confirm") &&
          (currentStep === "method" ? stepBlockers : blockers).map(key => (
            <Alert key={key} severity="warning" sx={{ mx: 5, mb: 2 }}>
              {t(key)}
            </Alert>
          ))}

        <MerchantInfo
          merchantName={service?.service_name}
          logoSrc={service?.logo?.url || DEFAULT_SERVICE_LOGO}
          borderColor={theme?.color_primary}
        />

        <Box
          sx={{
            border: 1,
            borderColor: theme?.color_primary,
            borderRadius: 1,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            px: { xs: 2, md: 8 },
            py: 4,
            bgcolor: theme?.light_gray,
          }}
        >
          {renderPanel(currentStep, methods, configuredFields)}
        </Box>

        {showsActions && (
          <Box
            sx={{
              bgcolor: "white",
              border: 1,
              borderColor: theme?.color_primary || "grey.300",
              borderRadius: 2,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              p: 2,
              mb: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Button
              onClick={() => navigate(-1)}
              variant="outlined"
              disabled={isLoading}
              sx={{ px: 3, py: 1.5, textTransform: "none" }}
            >
              {t("payment.actions.cancel")}
            </Button>

            <Box>
              {hasPreviousStep && (
                <Button
                  onClick={() => back(steps)}
                  variant="outlined"
                  disabled={isLoading}
                  sx={{
                    px: 3,
                    py: 1.5,
                    textTransform: "none",
                    borderColor: theme?.color_primary,
                    color: theme?.color_primary,
                    mr: 3,
                  }}
                >
                  {t("payment.actions.back")}
                </Button>
              )}
              <Button
                onClick={submit}
                disabled={!canSubmit}
                variant="contained"
                sx={{
                  bgcolor: canSubmit ? theme?.color_primary : "warning.main",
                  color: "#fff",
                  px: 3,
                  py: 1.5,
                  textTransform: "none",
                }}
              >
                {isLoading ? (
                  t("payment.actions.processing")
                ) : (
                  <>
                    {currentStep === "confirm"
                      ? t("schoolfee.confirm.pay")
                      : t("payment.actions.continue")}
                    <ArrowRight sx={{ ml: 1, fontSize: 20 }} />
                  </>
                )}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

// The priced confirmation. It separates the amount OWED from the amount the
// payer chose to pay, because a payer who authorises one number and is debited
// another has not agreed to the payment they made.
//
// ⛔ IT DOES NOT SHOW AN "AMOUNT OWED", removed 2026-09-10 at the maintainer's
// instruction, and the reason is worth keeping: the row rendered the FEE's
// face value under a heading that promises a BALANCE, and nothing in this
// product tracks a balance -- no part-payment already made is subtracted from
// it. A payer who had paid half their tuition last term would still have been
// shown the whole fee as what they owe. The feature that would make the label
// true is not implemented, so the number is not shown rather than shown
// wrongly. ⚠ It is removed on ALL THREE journeys, because the gap is the
// product's and not this journey's.
//
// ⚠ The unit comes from the SERVICE, not from the price quote being rendered:
// PriceResponse declares no `currency` (usePriceMutation.ts), so the service's
// own is the only one available before the pay response arrives. The pay
// response DOES carry one, and that is what the outcome screen renders.
const PricedConfirmation = ({ currency }: { currency?: string }) => {
  const { t } = useTranslation();
  const { selectedFee, amount } = useSchoolFee();

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" mb={2}>
        {t("schoolfee.steps.confirm")}
      </Typography>

      <StackList
        spacing={1}
        margin={0}
        list={[
          {
            title: t("schoolfee.fee.select_label"),
            value: selectedFee?.name ?? "-",
          },
          {
            title: t("schoolfee.confirm.amount_charged"),
            value: formatCurrency(amount || "0", undefined, currency),
          },
        ]}
      />

      <ServiceChargeSummary currency={currency} />
    </Box>
  );
};

/**
 * SCHOOLFEE_SEARCH — Journey A. The pupil is found on the school's roster, the
 * payer picks one of their applicable fees, sets an amount, chooses an
 * instrument and confirms a priced total.
 *
 * It mounts its own provider: `service.tsx` mounts only `PaymentProvider` and
 * is deliberately not touched.
 */
export const SchoolFeeSearchTemplate = (props: FlowTemplateProps) => (
  <SchoolFeeProvider>
    <SchoolFeeJourney
      {...props}
      steps={SCHOOL_FEE_SEARCH_STEPS}
      claims={SCHOOL_FEE_SEARCH_CLAIMS}
      scenario="A"
      identitySelfAdvances
      // A lapsed or absent search session. Nothing on the method screen fixes
      // it -- the remedy is to search again -- so it stops the payment.
      payBlockers={journey =>
        journey.handlerId.trim() === "" ? ["schoolfee.errors.no_session"] : []
      }
      identityPayload={journey => ({
        // D-HARM-6: the server compares student_uuid against the pupil the
        // handler_id session resolved. Both are read from the context that
        // wrote them together, never assembled from two sources here.
        handler_id: journey.handlerId,
        student_uuid: journey.studentUuid,
        // V1 accepts exactly one, and which fee the payer chose is the one
        // part of this payload a server cannot infer.
        fee_assignment_ids: journey.selectedFee
          ? [journey.selectedFee.fee_assignment_id]
          : [],
      })}
      renderPanel={(step, methods, configuredFields) => (
        <Paper
          elevation={1}
          sx={{
            p: 4,
            width: "100%",
            boxShadow: "none",
            bgcolor: props.theme?.light_gray,
          }}
        >
          {step === "identity" && (
            <StudentSearchForm coreServiceId={props.service?.core_service_id} />
          )}
          {step === "fee" && <FeeSelect />}
          {step === "amount" && (
            <SchoolFeeAmountForm serviceBounds={props.service?.amount} />
          )}
          {step === "method" && (
            <>
              <PaymentMethods payments={methods} />
              <SchoolFeeContactStep />
              {configuredFields}
            </>
          )}
          {step === "confirm" && (
            <PricedConfirmation currency={props.service?.currency} />
          )}
        </Paper>
      )}
    />
  </SchoolFeeProvider>
);

// Both declared-identity journeys are anchored on a placement the payer
// browsed to. It can only be empty if something navigated past the catalogue,
// so it stops the payment rather than the navigation.
const noPlacement = (journey: SchoolFeeJourneyState): string[] =>
  journey.selectionId.trim() === "" ? ["schoolfee.errors.no_selection"] : [];

/**
 * SCHOOLFEE_MANUAL — Journey B. The school's roster is not searchable, so the
 * payer browses the catalogue to their student's class, declares who the
 * student is, picks a fee and pays.
 *
 * ⛔ There is no search box on this journey and no affordance anywhere that
 * moves a payer between journeys (D-25, D-02). The registry chose this
 * template from the service's own triple; nothing here re-decides it, and
 * ⛔ nothing here reads `is_searchable`.
 */
export const SchoolFeeManualTemplate = (props: FlowTemplateProps) => (
  <SchoolFeeProvider>
    <SchoolFeeJourney
      {...props}
      steps={SCHOOL_FEE_MANUAL_STEPS}
      claims={SCHOOL_FEE_MANUAL_CLAIMS}
      scenario="B"
      stepTitles={{ identity: "schoolfee.manual.steps.identity" }}
      identitySatisfied={journey =>
        journey.selectionId.trim() !== "" &&
        isCompleteIdentity(
          {
            matricule: journey.matricule,
            firstName: journey.firstName,
            lastName: journey.lastName,
            dateOfBirth: journey.dateOfBirth,
          },
          { requiresMatricule: true }
        )
      }
      payBlockers={noPlacement}
      identityPayload={journey => ({
        // ⛔ No handler_id and no student_uuid: resolution is the selection_id
        // → offering path inside the subsidiary the server derives. D-HARM-5's
        // 300-second window and D-HARM-6's cross-check do not apply here, and
        // their absence is correct rather than an omission.
        selection_id: journey.selectionId,
        matricule: journey.matricule.trim(),
        first_name: journey.firstName.trim(),
        last_name: journey.lastName.trim(),
        // Already canonical YYYY-MM-DD: it is what a native date control
        // yields. The server also accepts an unpadded 2026-1-2 and
        // canonicalises it -- ⛔ that leniency is not a licence to send one.
        date_of_birth: journey.dateOfBirth,
        fee_assignment_ids: journey.selectedFee
          ? [journey.selectedFee.fee_assignment_id]
          : [],
      })}
      renderPanel={(step, methods, configuredFields) => (
        <Paper
          elevation={1}
          sx={{
            p: 4,
            width: "100%",
            boxShadow: "none",
            bgcolor: props.theme?.light_gray,
          }}
        >
          {step === "identity" && (
            <>
              <CatalogueBrowser
                coreServiceId={props.service?.core_service_id}
              />
              <ManualIdentityForm />
            </>
          )}
          {step === "fee" && (
            <CatalogueFeeSelect
              coreServiceId={props.service?.core_service_id}
              currency={props.service?.currency}
            />
          )}
          {step === "amount" && (
            <SchoolFeeAmountForm serviceBounds={props.service?.amount} />
          )}
          {step === "method" && (
            <>
              <PaymentMethods payments={methods} />
              <SchoolFeeContactStep />
              {configuredFields}
            </>
          )}
          {step === "confirm" && (
            <PricedConfirmation currency={props.service?.currency} />
          )}
        </Paper>
      )}
    />
  </SchoolFeeProvider>
);

/**
 * SCHOOLFEE_REGISTRATION — Journey C. A student who is not on the roster yet,
 * being registered into a placement, paying the registration fee in full.
 *
 * ⛔ NO matricule reaches the payload, and none is drawn: the server rejects a
 * registration payment that carries one, so it must not exist rather than be
 * hidden. `journey.matricule` is deliberately not read below.
 *
 * ⛔ NO amount step and no amount input anywhere (D-05). The fee is written
 * into the journey when it resolves and rendered as text on the fee screen;
 * `CPS-422-111` remains the actual enforcement.
 */
export const SchoolFeeRegistrationTemplate = (props: FlowTemplateProps) => (
  <SchoolFeeProvider>
    <SchoolFeeJourney
      {...props}
      steps={SCHOOL_FEE_REGISTRATION_STEPS}
      claims={SCHOOL_FEE_REGISTRATION_CLAIMS}
      scenario="C"
      stepTitles={{ identity: "schoolfee.registration.steps.identity" }}
      identitySatisfied={journey =>
        journey.selectionId.trim() !== "" &&
        isCompleteIdentity(
          {
            matricule: "",
            firstName: journey.firstName,
            lastName: journey.lastName,
            dateOfBirth: journey.dateOfBirth,
          },
          { requiresMatricule: false }
        )
      }
      payBlockers={noPlacement}
      identityPayload={journey => ({
        selection_id: journey.selectionId,
        first_name: journey.firstName.trim(),
        last_name: journey.lastName.trim(),
        date_of_birth: journey.dateOfBirth,
        fee_assignment_ids: journey.selectedFee
          ? [journey.selectedFee.fee_assignment_id]
          : [],
      })}
      renderPanel={(step, methods, configuredFields) => (
        <Paper
          elevation={1}
          sx={{
            p: 4,
            width: "100%",
            boxShadow: "none",
            bgcolor: props.theme?.light_gray,
          }}
        >
          {step === "identity" && (
            <>
              <CatalogueBrowser
                coreServiceId={props.service?.core_service_id}
              />
              <RegistrationIdentityForm />
            </>
          )}
          {step === "fee" && (
            <CatalogueFeeSelect
              coreServiceId={props.service?.core_service_id}
              currency={props.service?.currency}
              fixedFullAmount
            />
          )}
          {step === "method" && (
            <>
              <PaymentMethods payments={methods} />
              <SchoolFeeContactStep />
              {configuredFields}
            </>
          )}
          {step === "confirm" && (
            <PricedConfirmation currency={props.service?.currency} />
          )}
        </Paper>
      )}
    />
  </SchoolFeeProvider>
);

export default SchoolFeeSearchTemplate;
